import type { ProductFormState } from '../../types'

import React, { useCallback, useMemo, useEffect } from 'react'
import { mergeLeft } from 'merge-left-utils'
import { useFetchSupportedMarkCodeTypes } from '@@partner:graphql/query/catalog/supportedMarkCodeTypes.gql'

import { Checkbox, CheckboxGroup } from 'rsuite'
import AppLoader from '@components/common/AppLoader'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

const NON_SELECTED_VALUE = ''

export type ProductMarkingState = Pick<
  ProductFormState,
  'markable' | 'markTypes'
>
export interface ProductMarkingManagerProps {
  markable: boolean
  markTypes: string[]
  onChange?: (marking: ProductMarkingState) => void
}

export default function ProductMarkingManager ({
  markable,
  markTypes,
  onChange
}: ProductMarkingManagerProps) {
  const { data, loading, error } = useFetchSupportedMarkCodeTypes()
  const supportMarkCodeTypes = useMemo(
    () =>
      (data?.supportedMarkCodeTypes ?? []).map((code: string) => ({
        value: code,
        label: code
      })),
    [ data?.supportedMarkCodeTypes ]
  )

  const updateState = useCallback(
    (changes: Partial<ProductMarkingState>) => {
      onChange?.(
        mergeLeft<ProductMarkingState>({ markable, markTypes }, changes)
      )
    },
    [ markTypes, markable, onChange ]
  )

  const handleChangeIsMarkable = useCallback(
    (_, markable: boolean) => {
      if (markable === false) {
        updateState({ markable, markTypes: [] })
      }
      else {
        updateState({ markable })
      }
    },
    [ updateState ]
  )

  const handleSelectMarkTypes = useCallback(
    (selectedTypes: any[]) => {
      updateState({ markTypes: selectedTypes as string[] })
    },
    [ updateState ]
  )

  if (loading) {
    return <AppLoader content="Загрузка списка маркировок" />
  }

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title={'Не удалось загрузить список маркировок'}
        error={error}
      />
    )
  }

  return (
    <>
      <Checkbox checked={markable} onChange={handleChangeIsMarkable}>
        Товар подлежит маркировке
      </Checkbox>
      {markable && supportMarkCodeTypes?.length > 0 && (
        <div className="mt-3">
          <CheckboxGroup
            name="markTypes"
            value={markTypes}
            onChange={handleSelectMarkTypes}
          >
            {supportMarkCodeTypes.map((type) => (
              <Checkbox key={type.value} value={type.value}>
                {type.label}
              </Checkbox>
            ))}
          </CheckboxGroup>
        </div>
      )}
    </>
  )
}
